<template>
    <div>
        <section class="w-full bg-blue-100">
            <div class="bg-white rounded-b-2xl">
                <div class="self-gutter">
                    <div class="self-gutter-y self-content-sm">
                        <ReportNavigation v-if="this.$route.path.startsWith(`/${$i18n.locale}/reports`)" />
                        <h1 class="title-2xl">{{ $t('travels_report.title') }}</h1>
                        <AlertSummary
                            v-if="!generateReportOk"
                            :main-text="$t('error.main_text')"
                            :secondary-text="$t('error.secondary_text')"
                            alert-classes="mt-24 e5-form-error-summary--error"
                        />

                        <div class="pt-24 self-hero-intro expandable-description" :class="{ 'show-full-desc': showFullDesc }">
                            <p v-html="$t('travels_report.desc')"></p>
                            <a ref="showFullDesc" class="text-blue-400 cursor-pointer expand-link bg-white" style="font-style: italic">{{
                                $t('reports.read_more')
                            }}</a>
                        </div>
                        <!--Travels report form-->
                        <div class="pt-24 self-form-wrap">
                            <div class="w-full sm:w-5/12 self-form-wrap-item" :class="{ 'e5-error': !generateReportOk }">
                                <label class="self-form-item-label" for="calendar"
                                    >{{ $t('reports.form.period') }}:<span class="e5-form-field-required">*</span></label
                                >
                                <div class="relative">
                                    <button
                                        ref="calendarButton"
                                        id="calendar"
                                        class="e5-input-calendar"
                                        :class="{ 'e5-error': !generateReportOk }"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                        @click="dropdown('calendarButton')"
                                    >
                                        <span :class="{ 'text-alert': !generateReportOk }">
                                            {{
                                                ServiceByDateRequest.service.servicestartdate || ServiceByDateRequest.service.serviceenddate
                                                    ? selectedDateRange
                                                    : $t('reports.form.period_placeholder')
                                            }}</span
                                        >
                                    </button>

                                    <div class="e5-input-icon">
                                        <CalendarSVG />
                                    </div>
                                    <DropDownCalendar v-model="selectedDateRange" @input="handleDateChange" />
                                </div>
                                <a class="hover:text-blue-400 cursor-pointer" @click="quickSelectDates('currentMonth')">{{
                                    $t('reports.form.current_month')
                                }}</a>
                                |
                                <a class="hover:text-blue-400 cursor-pointer" @click="quickSelectDates('lastMonth')">{{
                                    $t('reports.form.last_month')
                                }}</a>
                                |
                                <a class="hover:text-blue-400 cursor-pointer" @click="quickSelectDates('currentYear')">{{
                                    $t('reports.form.current_year')
                                }}</a>
                                |
                                <a class="hover:text-blue-400 cursor-pointer" @click="quickSelectDates('lastYear')">{{
                                    $t('reports.form.last_year')
                                }}</a>
                            </div>

                            <div class="w-1/2 sm:w-4/12 self-form-wrap-item">
                                <label class="self-form-item-label" for="destination">{{ $t('reports.form.destination') }}:</label>
                                <div class="relative">
                                    <div class="self-form-select-wrap">
                                        <select name="destination" id="destination" class="self-multiselect" multiple>
                                            <option v-bind:value="''"></option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div class="w-1/2 sm:w-3/12 self-form-wrap-item">
                                <label class="self-form-item-label" for="ticket">
                                    {{ $t('reports.form.ticket_number_booking_ref') }}:
                                </label>
                                <div class="relative">
                                    <input
                                        type="text"
                                        id="ticket"
                                        placeholder=""
                                        name="ticket"
                                        class="self-form-input"
                                        v-model="ServiceByDateRequest.ticket.ticketNo"
                                    />
                                </div>
                            </div>
                            <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                <label class="self-form-item-label" for="passenger_name">{{ $t('reports.form.passenger_name') }}:</label>
                                <div class="relative">
                                    <input
                                        type="text"
                                        id="passenger_name"
                                        placeholder=""
                                        name="passenger_name"
                                        class="self-form-input"
                                        v-model="ServiceByDateRequest.passenger.name"
                                    />
                                </div>
                            </div>
                            <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                <label class="self-form-item-label" for="reference">{{ $t('reports.form.passenger_ref_no') }}:</label>
                                <div class="relative">
                                    <input
                                        type="text"
                                        id="reference"
                                        placeholder=""
                                        name="reference"
                                        class="self-form-input"
                                        v-model="ServiceByDateRequest.passenger.reference"
                                    />
                                </div>
                            </div>
                            <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                <label class="self-form-item-label" for="booked">{{ $t('reports.form.booker_name') }}:</label>
                                <div class="relative">
                                    <input
                                        type="text"
                                        id="booked"
                                        placeholder=""
                                        name="booked"
                                        class="self-form-input"
                                        v-model="ServiceByDateRequest.booking.bookedbyname"
                                    />
                                </div>
                            </div>
                            <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                <label class="self-form-item-label" for="customer">{{ $t('reports.form.customer_ref') }}:</label>
                                <div class="relative">
                                    <input
                                        type="text"
                                        id="customer"
                                        placeholder=""
                                        name="customer"
                                        class="self-form-input"
                                        v-model="ServiceByDateRequest.booking.customerreference"
                                    />
                                </div>
                            </div>
                            <div aria-labelledby="hiddenrow" class="flex flex-wrap w-full max-h-0 overflow-hidden">
                                <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                    <label class="self-form-item-label" for="code">{{ $t('reports.form.project_code') }}:</label>
                                    <div class="relative">
                                        <input
                                            type="text"
                                            id="code"
                                            placeholder=""
                                            name="code"
                                            class="self-form-input"
                                            v-model="ServiceByDateRequest.booking.projectcode"
                                        />
                                    </div>
                                </div>
                                <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                    <label class="self-form-item-label" for="budget">{{ $t('reports.form.budget_code') }}:</label>
                                    <div class="relative">
                                        <input
                                            type="text"
                                            id="budget"
                                            placeholder=""
                                            name="budget"
                                            class="self-form-input"
                                            v-model="ServiceByDateRequest.booking.budgetcode"
                                        />
                                    </div>
                                </div>

                                <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                    <label class="self-form-item-label" for="account">{{ $t('reports.form.fin_account') }}:</label>
                                    <div class="relative">
                                        <input
                                            type="text"
                                            id="account"
                                            placeholder=""
                                            name="account"
                                            class="self-form-input"
                                            v-model="ServiceByDateRequest.booking.finaccount"
                                        />
                                    </div>
                                </div>
                                <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                    <label class="self-form-item-label" for="me">{{ $t('reports.form.me_number') }}:</label>
                                    <div class="relative">
                                        <input
                                            type="text"
                                            id="me"
                                            placeholder=""
                                            name="me"
                                            class="self-form-input"
                                            v-model="ServiceByDateRequest.booking.menumber"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="self-form-wrap-actions">
                                <div>
                                    <button
                                        v-if="$store.state.invoiceExtraFields"
                                        ref="animateToggleButton"
                                        aria-expanded="false"
                                        id="hiddenrow"
                                        class="self-btn-plain"
                                        @click="animatetoggle('animateToggleButton')"
                                        data-toggle="animatetoggle"
                                    >
                                        <span class="self-btn-plain__text">{{ $t('reports.form.more_options') }}</span>
                                        <span class="self-btn-plain__icon self-moreinfo-icon"><IconArrowForwardSVG /></span>
                                    </button>
                                </div>

                                <div class="flex xs-down:justify-end xs-down:pt-24">
                                    <a class="mr-24 self-btn-secondary hover:cursor-pointer" @click="clearAll">{{
                                        $t('reports.form.clear_all')
                                    }}</a>

                                    <button class="self-btn-primary" :disabled="loading" @click="generateOnClick(ServiceByDateRequest)">
                                        {{ $t('reports.form.generate_report') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <!--    Not empty response    -->
        <article v-if="serviceByDateResponse && requestSent">
            <div id="resultsTable" class="relative bg-blue-100 rounded-b-2xl self-gutter-y">
                <div class="self-gutter">
                    <div class="self-content">
                        <div class="self-table-info">
                            <div class="flex items-center sm-down:w-full sm-down:justify-between">
                                <span class="block text-base"
                                    >{{ $t('reports.table.total') }}:
                                    <span class="font-semibold whitespace-nowrap">{{
                                        formatSum(totalSum(serviceByDateResponse))
                                    }}</span></span
                                >

                                <div class="self-table-info-entries-count"></div>
                            </div>

                            <div class="flex items-center sm-down:w-full sm-down:justify-between sm-down:pt-16">
                                <div class="self-table-info-entries-select"></div>

                                <div class="relative inline-block">
                                    <div class="relative inline-block">
                                        <button
                                            ref="exportButton"
                                            class="self-btn-plain"
                                            id="export-report"
                                            aria-haspopup="true"
                                            aria-expanded="false"
                                            @click="dropdown('exportButton')"
                                        >
                                            <span class="self-btn-plain__text">{{ $t('reports.table.export') }}</span>
                                            <span class="self-btn-plain__icon self-moreinfo-icon">
                                                <IconArrowForwardSVG />
                                            </span>
                                        </button>

                                        <div class="e5-dropdown e5-dropdown-xs" aria-labelledby="export-report">
                                            <div class="p-10">
                                                <a
                                                    class="py-5 self-text-link hover:cursor-pointer"
                                                    @click="download(serviceByDateResponse, '.xlsx', 'travel')"
                                                    >Excel (XLSX)</a
                                                >
                                                <a
                                                    class="py-5 self-text-link hover:cursor-pointer"
                                                    @click="download(serviceByDateResponse, '.csv', 'travel')"
                                                    >CSV</a
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <table class="w-full self-table">
                            <thead>
                                <tr>
                                    <th class="date">
                                        <span>{{ $t('reports.table.service_start_date') }}</span>
                                    </th>
                                    <th class="date">
                                        <span>{{ $t('reports.table.service_end_date') }}</span>
                                    </th>
                                    <th>
                                        <span>
                                            <select id="passengerSelect" class="self-table-header-select">
                                                <option value="">{{ $t('reports.table.passenger_name') }}</option>
                                                <option v-for="passenger in uniquePassengers" :key="passenger" :value="passenger">
                                                    {{ passenger }}
                                                </option>
                                            </select>
                                        </span>
                                    </th>
                                    <th>
                                        <span>
                                            <select id="destinationSelect" class="self-table-header-select">
                                                <option value="">{{ $t('reports.table.all_destinations') }}</option>
                                                <option v-for="country in uniqueCountries" :key="country" :value="country">
                                                    {{ country }}
                                                </option>
                                            </select>
                                        </span>
                                    </th>
                                    <th>
                                        <span>
                                            <select id="serviceSelect" class="self-table-header-select">
                                                <option value="">{{ $t('reports.table.all_services') }}</option>
                                                <option v-for="service in uniqueServices" :key="service" :value="service">
                                                    {{ service }}
                                                </option>
                                            </select>
                                        </span>
                                    </th>
                                    <th>
                                        <span
                                            ><select id="documentSelect" class="self-table-header-select">
                                                <option value="">{{ $t('reports.table.documents') }}</option>
                                                <option v-for="documentNo in uniqueDocuments" :key="documentNo" :value="documentNo">
                                                    {{ documentNo }}
                                                </option>
                                            </select></span
                                        >
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in serviceByDateResponse?.services ?? []" :key="item.service_id">
                                    <td>{{ formatDate(item.servicestartdate) }}</td>
                                    <td>{{ formatDate(item.serviceenddate) }}</td>
                                    <td v-if="item.servicepassengerlist[0]">
                                        <p v-for="passenger in item.servicepassengerlist" :key="passenger.passengerId">
                                            {{ passenger.name }}<br />
                                        </p>
                                    </td>
                                    <td v-else></td>
                                    <td v-if="item.servicedestinationcountry">
                                        {{ item.servicedestinationcountry + ', ' + item.servicedestinationcity }}
                                    </td>
                                    <td v-else></td>
                                    <td>{{ item.productname }}</td>
                                    <td v-if="item.documents.length > 0">
                                        <span v-for="document in item.documents" :key="document.documentId">
                                            <a
                                                :href="document.documentId"
                                                @click.prevent="
                                                    downloadDocument(document.documentId, document.documentType, document.documentNo)
                                                "
                                            >
                                                {{ document.documentNo }}
                                            </a>
                                            {{ getDocumentTypeTranslation(document.documentType) }} <br />
                                        </span>
                                    </td>
                                    <td v-else></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </article>
        <!--    Empty response   -->
        <article v-else>
            <section id="resultsTable" class="relative bg-blue-100 rounded-b-2xl self-gutter-y">
                <div class="self-gutter">
                    <div class="self-content-sm">
                        <span class="block title-2xl pt-32 pb-[300px] text-black font-normal" v-if="requestSent && responseReceived">{{
                            $t('reports.table.no_results')
                        }}</span>
                        <span class="block title-2xl pt-32 pb-[300px] text-black font-normal" v-else-if="requestSent">
                            <span class="loading-animation"
                                >{{ $t('reports.table.searching')
                                }}<span class="loading-dots">
                                    <span class="dot">.</span>
                                    <span class="dot">.</span>
                                    <span class="dot">.</span>
                                </span></span
                            ></span
                        >
                    </div>
                </div>
            </section>
        </article>
    </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { IToplevel } from '@/domain/IToplevel';
import CalendarSVG from '@/assets/static/icons-src/icon-calendar.svg';
import IconArrowForwardSVG from '@/assets/static/icons-src/icon-arrow-forward.svg';
import ArrowLeftSVG from '@/assets/static/icons-src/est-arrow-left.svg';
import LogoIllustrationSVG from '@/assets/static/icons-src/logo-illustration.svg';
import store from '@/store';
import { IReportServiceByDateResponce, IServiceByDateRequest } from '@/domain/IReport';
import { IPassenger } from '@/domain/IPassenger';
import { IBooking } from '@/domain/IBooking';
import { ITicket } from '@/domain/ITicket';
import { IService } from '@/domain/IService';
import $ from 'jquery';
import DropDownCalendar from '@/components/DropDownCalendar.vue';
import 'select2';
import UtilsMixin from '@/scripts/utils';
import moment from 'moment';
import ReportNavigation from '@/components/ReportNavigation.vue';
import Choices from 'choices.js';
import AlertSummary from '@/components/AlertSummary.vue';
import { eventBus } from '@/../eventBus';

@Component({
    components: {
        ReportNavigation,
        DropDownCalendar,
        CalendarSVG,
        IconArrowForwardSVG,
        ArrowLeftSVG,
        LogoIllustrationSVG,
        AlertSummary
    },
    metaInfo: {
        title: 'Overview of travels'
    },
    mixins: [UtilsMixin]
})
export default class TravelsReport extends Vue {
    // Properties
    selectedDateRange = '';
    requestSent = false;
    private initExtraTable = true;
    generateReportOk = true;
    private extraTable = null;
    responseReceived = false;
    private selectedCountryCode: string | number | string[] = [];
    showFullDesc = false;
    loading = false;
    ServiceByDateRequest: IServiceByDateRequest = {
        invoicedServices: '0',
        saleschannels: '',
        datetype: 'SERVICE_DATE',
        passenger: {
            passengerId: '',
            name: '',
            birthday: '',
            reference: ''
        } as unknown as IPassenger,
        booking: {
            bookingnumber: '',
            bookedbyname: '',
            customerreference: '',
            projectcode: '',
            budgetcode: '',
            employeenumber: '',
            menumber: '',
            finaccount: ''
        } as unknown as IBooking,
        toplevel: {
            toplevel_id: '',
            toplevel_name: ''
        } as unknown as IToplevel[],
        ticket: {
            ticketNo: ''
        } as unknown as ITicket,
        service: {
            id: '',
            servicestartdate: '',
            serviceenddate: '',
            bookingreferencenumber: '',
            servicedestinationcountry: '',
            servicedestinationcity: '',
            productname: ''
        } as unknown as IService,
        customerid: store.state.customerId
    };

    serviceByDateResponse: IReportServiceByDateResponce | null = null;

    // Computed properties
    get uniquePassengers(): string[] {
        return Array.from(
            new Set(
                (this.serviceByDateResponse?.services ?? [])
                    .flatMap((item) => item.servicepassengerlist.map((passenger) => passenger.name))
                    .sort()
            )
        ).filter(Boolean);
    }

    get uniqueServices(): string[] {
        return Array.from(new Set((this.serviceByDateResponse?.services ?? []).map((item) => item.productname)))
            .filter(Boolean)
            .sort();
    }

    get uniqueCountries(): string[] {
        return Array.from(new Set((this.serviceByDateResponse?.services ?? []).map((item) => item.servicedestinationcountry)))
            .filter(Boolean)
            .sort();
    }

    get uniqueDocuments(): string[] {
        const allDocumentNos = (this.serviceByDateResponse?.services ?? []).flatMap((item) => item.documents.map((doc) => doc.documentNo));
        const uniqueDocumentNos = Array.from(new Set(allDocumentNos));
        return uniqueDocumentNos.sort();
    }

    // Methods

    async generateOnClick(ServiceByDateRequest: IServiceByDateRequest) {
        if (this.selectedDateRange.split('—').length !== 2) {
            this.generateReportOk = false;
            this.scrollToAlertMessage();
        } else {
            this.scrollToResultsTable();
            this.ServiceByDateRequest.customerid = store.getters.getCustomerId;
            this.generateReportOk = true;
            this.requestSent = true;
            this.initExtraTable = true;
            this.destroyExtraTable();
            if (Array.isArray(this.selectedCountryCode)) {
                this.ServiceByDateRequest.service.servicedestinationcountry =
                    this.selectedCountryCode.length > 0 ? this.selectedCountryCode[0] : '';
            }
            this.serviceByDateResponse = null;
            this.responseReceived = false;
            this.loading = true;
            this.serviceByDateResponse = await store.dispatch('getServicesByDate', ServiceByDateRequest);
            this.loading = false;
            this.responseReceived = true;
        }
    }

    // Type Declarations for TypeScript to tell that these methods exist in utils.ts
    scrollToAlertMessage!: () => void;
    scrollToResultsTable!: () => void;
    initDescriptionExpansion!: () => void;
    quickSelectDates!: (range: string) => void;

    clearAll() {
        this.ServiceByDateRequest = {
            invoicedServices: '0',
            saleschannels: '',
            datetype: 'SERVICE_DATE',
            passenger: {
                passengerId: '',
                name: '',
                birthday: '',
                reference: ''
            } as unknown as IPassenger,
            booking: {
                bookingnumber: '',
                bookedbyname: '',
                customerreference: '',
                projectcode: '',
                budgetcode: '',
                employeenumber: '',
                menumber: '',
                finaccount: ''
            } as unknown as IBooking,
            toplevel: {
                toplevel_id: '',
                toplevel_name: ''
            } as unknown as IToplevel[],
            ticket: {
                ticketNo: ''
            } as unknown as ITicket,
            service: {
                id: '',
                servicestartdate: '',
                serviceenddate: '',
                bookingreferencenumber: '',
                servicedestinationcountry: '',
                servicedestinationcity: '',
                productname: ''
            } as unknown as IService,
            customerid: store.state.customerId
        };
        this.requestSent = false;
        this.$root.$emit('reset-calendar');
        this.selectedDateRange = '';
        this.destroyExtraTable();
        this.initExtraTable = true;
        this.responseReceived = false;
        if (Array.isArray(this.selectedCountryCode)) {
            if (this.choices && this.selectedCountryCode.length > 0) {
                this.choices.removeActiveItemsByValue(this.selectedCountryCode[0]);
            }
        }
        this.selectedCountryCode = [];
        this.serviceByDateResponse = null;
    }

    private choices = null;

    private initMultiSelects() {
        if (this.choices) {
            this.choices.destroy();
        }
        const selectElement = document.querySelector('.self-multiselect') as HTMLSelectElement;
        if (selectElement === null) return;
        const placeholderText = this.$t('reports.form.choose_destination') as string;
        this.choices = new Choices(document.querySelector('.self-multiselect'), {
            removeItemButton: true,
            itemSelectText: placeholderText,
            maxItemCount: 2,
            maxItemText: this.$t('reports.form.only_one_can_be_selected') as string,
            noChoicesText: this.$t('reports.form.no_choices_to_choose_from') as string,
            allowHTML: true
        });

        this.choices.passedElement.element.addEventListener(
            'change',
            (event) => {
                const selectedValues = $(event.target).val() as string[];
                if (selectedValues.length > 1) {
                    // Convert the HTMLSelectElement to an array-like object
                    const options = Array.from(selectElement.options);

                    // Find the option with the specified value and remove it
                    const optionToRemove = options.find((option) => option.value === selectedValues[0]);
                    if (optionToRemove) {
                        selectElement.removeChild(optionToRemove);
                    }

                    // Update Choices.js instance to reflect the changes
                    this.choices.removeActiveItemsByValue(selectedValues[0]);
                    selectedValues.splice(0, 1);
                    this.choices.setChoiceByValue(selectedValues);
                }
                this.selectedCountryCode = selectedValues;
            },
            false
        );

        this.choices.setChoices(store.state.country, 'country_code', 'country_name');
    }

    private createExtraTable() {
        $.fn.dataTable.ext.errMode = 'throw';

        // Sorting function for dates in 'DD.MM.YYYY' format
        $.fn.dataTable.ext.type.order['moment-date-asc'] = function (a, b) {
            return moment(a, 'DD.MM.YYYY').isAfter(moment(b, 'DD.MM.YYYY')) ? 1 : -1;
        };

        $.fn.dataTable.ext.type.order['moment-date-desc'] = function (a, b) {
            return moment(a, 'DD.MM.YYYY').isBefore(moment(b, 'DD.MM.YYYY')) ? 1 : -1;
        };

        // Filters
        $.fn.dataTable.ext.search.push(function (settings, data, dataIndex) {
            const selectedPassenger = $('#passengerSelect').val();
            if (settings.aoColumns.length < 3) {
                return true;
            }
            if (selectedPassenger === '') {
                return true;
            }
            const name = data[2].trim() || '';
            return name === selectedPassenger || name.includes(selectedPassenger);
        });
        $.fn.dataTable.ext.search.push(function (settings, data, dataIndex) {
            const selectedCountry = $('#destinationSelect').val();
            if (settings.aoColumns.length < 4) {
                return true;
            }
            if (selectedCountry === '') {
                return true;
            }

            const destinationCountryAndCity = data[3] || '';
            const destinationCountry = destinationCountryAndCity.split(',')[0].trim();
            return destinationCountry === selectedCountry;
        });
        $.fn.dataTable.ext.search.push(function (settings, data, dataIndex) {
            const selectedService = $('#serviceSelect').val();
            if (settings.aoColumns.length < 5) {
                return true;
            }
            if (selectedService === '') {
                return true;
            }
            const service = data[4] || '';
            return service === selectedService;
        });
        $.fn.dataTable.ext.search.push(function (settings, data, dataIndex) {
            const selectedCountry = $('#documentSelect').val();
            if (settings.aoColumns.length < 6) {
                return true;
            }
            if (selectedCountry === '') {
                return true;
            }
            const name = data[5].trim() || '';
            return name === selectedCountry || name.includes(selectedCountry);
        });
        this.destroyExtraTable();
        this.extraTable = $('.self-table').DataTable({
            dom: '<il<t>p>',
            paging: true,
            info: true,
            scrollX: true,
            order: [],
            language: {
                lengthMenu: this.$t('datatable.lengthMenu') as string,
                info: this.$t('datatable.info') as string,
                infoEmpty: this.$t('datatable.infoEmpty') as string,
                zeroRecords: this.$t('datatable.zeroRecords') as string,
                paginate: {
                    first: this.$t('datatable.first') as string,
                    last: this.$t('datatable.last') as string,
                    next: this.$t('datatable.next') as string,
                    previous: this.$t('datatable.previous') as string
                }
            },
            columnDefs: [
                {
                    type: 'moment-date',
                    targets: 'date'
                }
            ]
        });
        // Clear the previously appended elements
        const entriesCount = document.querySelector('.self-table-info-entries-count');
        const lengthSelect = document.querySelector('.self-table-info-entries-select');

        if (entriesCount) {
            entriesCount.innerHTML = '';
        }

        if (lengthSelect) {
            lengthSelect.innerHTML = '';
        }

        const tables = document.querySelectorAll('.dataTables_wrapper');

        tables.forEach((table) => {
            const prev = table.previousElementSibling;
            const entriesCount = prev.querySelector('.self-table-info-entries-count');
            const lengthSelect = prev.querySelector('.self-table-info-entries-select');

            $(table).find('.dataTables_info').appendTo(entriesCount);
            $(table).find('.dataTables_length').appendTo(lengthSelect);
        });
        const selectTags = document.querySelectorAll('.self-table-header-select');
        selectTags.forEach((selectTag) => {
            if (selectTag) {
                selectTag.addEventListener('click', (event) => {
                    event.stopPropagation();
                });
            }
        });
        $('#passengerSelect').on('change', () => {
            this.extraTable.draw();
        });
        $('#destinationSelect').on('change', () => {
            this.extraTable.draw();
        });
        $('#serviceSelect').on('change', () => {
            this.extraTable.draw();
        });
        $('#documentSelect').on('change', () => {
            this.extraTable.draw();
        });
    }

    private destroyExtraTable() {
        if (this.extraTable != null) {
            this.extraTable.destroy();
            this.extraTable = null;
        }
    }

    async onLocaleChange() {
        this.initMultiSelects();
        this.createExtraTable();
        this.initDescriptionExpansion();
    }

    // Displaying document types
    getDocumentTypeTranslation(documentType: string): string {
        switch (documentType) {
            case 'AIR_TICKET':
                return (('(' + this.$t('reports.table.document_types.AIR_TICKET')) as string) + ')';
            case 'INVOICE':
                return (('(' + this.$t('reports.table.document_types.INVOICE')) as string) + ')';
            case 'ITINERARY':
                return (('(' + this.$t('reports.table.document_types.ITINERARY')) as string) + ')';
            case 'VOUCHER':
                return (('(' + this.$t('reports.table.document_types.VOUCHER')) as string) + ')';
            default:
                return ''; // Handle other cases if necessary
        }
    }

    // ============ Lifecycle methods ==========

    async mounted() {
        eventBus.$on('dataFetched', this.onLocaleChange);
        this.clearAll();
        this.initMultiSelects();
        this.initDescriptionExpansion();
        this.quickSelectDates('yearFromNow');
        this.generateOnClick(this.ServiceByDateRequest);
    }

    beforeDestroy() {
        eventBus.$off('dataFetched', this.onLocaleChange);
    }

    updated(): void {
        if (this.serviceByDateResponse && this.requestSent && this.initExtraTable) {
            this.createExtraTable();
            this.initExtraTable = false;
        }
    }

    destroyed(): void {
        $.fn.dataTable.ext.search.length = 0; // Clear the array of custom search functions
        if (this.choices) this.choices.destroy();
        this.clearAll();
    }
}
</script>

<style scoped></style>
